/* eslint-disable no-nested-ternary */
import React, { useState, useEffect } from 'react';
import {
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Text,
  useToast,
  HStack,
  Stat,
  StatLabel,
  StatNumber,
  Card,
  CardBody,
  Icon,
  Divider,
  Container,
  VStack,
  Badge,
  useColorModeValue,
  StatHelpText,
  SimpleGrid,
  Stack,
  StackItem,
  Tooltip,
  Select,
} from '@chakra-ui/react';
import { useAuthUser } from 'core';
import { Api } from 'api';
import { DownloadIcon, SearchIcon, InfoIcon } from '@chakra-ui/icons';
import {
  DateRangePreSelector,
  DataTable,
  LoadingIndicator,
  ExpertisePointSelect,
  LocalizedRangeDatepicker,
} from 'components';
import { createColumnHelper } from '@tanstack/react-table';
import { ExpertisePoint, ExpertisePointGroupTypeLabel } from 'types';
import { t, Trans } from '@lingui/macro';
import {
  FinancialSummaryReportResponse,
  GetFinancialSummaryReportRequest,
  FinancialSummaryOrderDetailDto,
} from '../services/financialSummaryReport.types';

const formatDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const AdminFinancialSummaryReportPage: React.FC = () => {
  const [selectedDates, setSelectedDates] = useState<Date[]>([
    new Date(Date.now() - 7 * 24 * 60 * 60 * 1000), // 7 gün öncesi
    new Date(),
  ]);
  const [selectedBrand, setSelectedBrand] = useState<number>(0);
  const [selectedExpertisePoint, setSelectedExpertisePoint] = useState<ExpertisePoint | undefined>(undefined);
  const [reportData, setReportData] = useState<FinancialSummaryReportResponse | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [autoSearch, setAutoSearch] = useState<boolean>(true);

  const toast = useToast();
  const authUserInfo = useAuthUser();

  // Dark mode uyumlu renkler
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const headingColor = useColorModeValue('gray.800', 'white');
  const textColor = useColorModeValue('gray.800', 'gray.100');
  const badgeBg = useColorModeValue('blue.50', 'blue.900');
  const formBg = useColorModeValue('gray.50', 'gray.900');

  // Renk şeması
  const colors = {
    positive: useColorModeValue('green.600', 'green.300'),
    negative: useColorModeValue('red.600', 'red.300'),
    neutral: useColorModeValue('blue.600', 'blue.300'),
    accent: useColorModeValue('teal.600', 'teal.300'),
    highlight: useColorModeValue('orange.600', 'orange.300'),
    cardBg1: useColorModeValue('blue.50', 'blue.900'),
    cardBg2: useColorModeValue('green.50', 'green.900'),
    cardBg3: useColorModeValue('teal.50', 'teal.900'),
    cardBg4: useColorModeValue('orange.50', 'orange.900'),
    cardBg5: useColorModeValue('red.50', 'red.900'),
    cardBg6: useColorModeValue('cyan.50', 'cyan.900'),
    tableBgEven: useColorModeValue('white', 'gray.800'),
    tableBgOdd: useColorModeValue('gray.50', 'gray.700'),
    tableHeaderBg: useColorModeValue('gray.700', 'gray.600'),
    tableHeaderText: useColorModeValue('white', 'white'),
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const handleSearch = async () => {
    if (!selectedDates || selectedDates.length !== 2) {
      toast({
        title: t`Uyarı`,
        description: t`Lütfen başlangıç ve bitiş tarihlerini seçin.`,
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsLoading(true);

    try {
      const request: GetFinancialSummaryReportRequest = {
        startDate: formatDate(selectedDates[0]),
        endDate: formatDate(selectedDates[1]),
        groupType: selectedBrand === 0 ? undefined : selectedBrand,
      };

      if (selectedExpertisePoint) {
        request.expertisePointId = selectedExpertisePoint.id;
      } else if (!authUserInfo?.isSuperAdmin && !authUserInfo?.isAdmin && authUserInfo?.expertisePointId) {
        // Normal kullanıcılar için kendi bayi ID'sini kullan
        request.expertisePointId = authUserInfo.expertisePointId;
      }

      const response = await Api.Reporting.getFinancialSummaryReport(request);
      setReportData(response.data);
    } catch (error) {
      toast({
        title: t`Hata`,
        description: t`Rapor verileri yüklenirken bir hata oluştu.`,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Otomatik arama
  useEffect(() => {
    // Admin ve süper admin kullanıcılar için otomatik arama yapmayalım
    if (autoSearch && selectedDates.length === 2) {
      // Sadece normal kullanıcılar (bayi kullanıcıları) için otomatik arama yapalım
      if (!authUserInfo?.isSuperAdmin && !authUserInfo?.isAdmin && authUserInfo?.expertisePointId) {
        handleSearch();
      }
      setAutoSearch(false);
    }
  }, [autoSearch, authUserInfo]);

  const handleExport = async () => {
    if (!selectedDates || selectedDates.length !== 2) {
      toast({
        title: t`Uyarı`,
        description: t`Lütfen başlangıç ve bitiş tarihlerini seçin.`,
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsExporting(true);

    try {
      const request: GetFinancialSummaryReportRequest = {
        startDate: formatDate(selectedDates[0]),
        endDate: formatDate(selectedDates[1]),
        groupType: selectedBrand === 0 ? undefined : selectedBrand,
      };

      if (selectedExpertisePoint) {
        request.expertisePointId = selectedExpertisePoint.id;
      } else if (!authUserInfo?.isSuperAdmin && !authUserInfo?.isAdmin && authUserInfo?.expertisePointId) {
        // Normal kullanıcılar için kendi bayi ID'sini kullan
        request.expertisePointId = authUserInfo.expertisePointId;
      }

      const blob = await Api.Reporting.exportFinancialSummaryReport(request);

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'bayi-bakiye-raporu.xlsx';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast({
        title: 'Başarılı',
        description: 'Rapor başarıyla indirildi.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Hata',
        description: 'Rapor indirilirken bir hata oluştu.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsExporting(false);
    }
  };

  // Tarih aralığı değiştiğinde çağrılacak fonksiyon
  const handleDateChange = (dates: Date[]) => {
    // Eğer tarih aralığı değiştiyse, mevcut rapor verilerini sıfırla
    if (
      dates.length === 2 &&
      (dates[0].getTime() !== selectedDates[0]?.getTime() || dates[1].getTime() !== selectedDates[1]?.getTime())
    ) {
      setReportData(null);
    }

    setSelectedDates(dates);
  };

  // Define a type for the table data
  type FinancialTableRow = {
    type: string;
    count: number | null;
    amount: number;
    rowType: string;
  };

  // Tablo sütunları için column helper
  const columnHelper = createColumnHelper<FinancialTableRow>();

  // Tablo sütunlarını tanımlama
  const columns = [
    columnHelper.accessor('type', {
      header: t`İşlem Türü`,
      cell: (info) => {
        const type = info.getValue();
        // Önemli satırlar için kalın yazı tipi
        const isBold = [
          'Devredilen Bakiye',
          'Yüklenen Bakiye',
          'Toplam Bakiye',
          'Gider Toplam',
          'Son Bakiye',
          'Ekspertiz Komisyon Tutarı (Royalty)',
          'e-Mağaza Harcaması',
        ].includes(type);
        return <Text fontWeight={isBold ? 'bold' : 'normal'}>{type}</Text>;
      },
    }),
    columnHelper.accessor('count', {
      header: t`Miktar`,
      cell: (info) => {
        const value = info.getValue();
        const { type } = info.row.original;
        if (value === null || value === undefined) return '-';
        return `${value} ${type.includes('Sorgu') ? 'Sorgu' : type.includes('Araç') ? 'Araç' : 'Adet'}`;
      },
      meta: {
        isNumeric: true,
      } as any,
    }),
    columnHelper.accessor('amount', {
      header: t`Tutar`,
      cell: (info) => {
        const value = info.getValue();
        const { type } = info.row.original;

        // Önemli satırlar için kalın yazı tipi
        const isBold = [
          'Devredilen Bakiye',
          'Yüklenen Bakiye',
          'Toplam Bakiye',
          'Gider Toplam',
          'Son Bakiye',
          'Ekspertiz Komisyon Tutarı (Royalty)',
          'e-Mağaza Harcaması',
        ].includes(type);

        // Satır tipine göre renk belirleme
        let color;
        if (type === 'Devredilen Bakiye' || type === 'Son Bakiye') {
          color = colors.neutral;
        } else if (type === 'Yüklenen Bakiye') {
          color = colors.positive;
        } else if (type === 'Toplam Bakiye') {
          color = colors.accent;
        } else if (type === 'Gider Toplam') {
          color = colors.negative;
        } else if (type === 'e-Mağaza Harcaması') {
          color = colors.highlight;
        } else {
          color = value < 0 ? colors.negative : colors.positive;
        }

        return (
          <Text fontWeight={isBold ? 'bold' : 'normal'} color={color}>
            {formatCurrency(value)}
          </Text>
        );
      },
      meta: {
        isNumeric: true,
      } as any,
    }),
  ];

  // Tablo verilerini hazırlama - satır renklerini belirlemek için rowType ekleyelim
  const prepareTableData = () => {
    if (!reportData) return [];

    return [
      { type: t`Devredilen Bakiye`, count: null, amount: reportData.initialBalance, rowType: 'cardBg1' },
      { type: t`Yüklenen Bakiye`, count: null, amount: reportData.loadedBalance, rowType: 'cardBg2' },
      { type: t`Toplam Bakiye`, count: null, amount: reportData.totalBalance, rowType: 'cardBg3' },
      { type: 'Ekspertiz Komisyon Tutarı (Royalty)', count: null, amount: reportData.royaltyAmount, rowType: 'even' },
      {
        type: t`Hasar Sorgu`,
        count: reportData.damageQuery.count,
        amount: reportData.damageQuery.amount,
        rowType: 'odd',
      },
      {
        type: t`Araç Detay Sorgulama (Tramer360)`,
        count: reportData.tramer360Query.count,
        amount: reportData.tramer360Query.amount,
        rowType: 'even',
      },
      {
        type: t`Kilometre Sorgu`,
        count: reportData.kilometerQuery.count,
        amount: reportData.kilometerQuery.amount,
        rowType: 'odd',
      },
      {
        type: t`Yol Yardım Paketi`,
        count: reportData.roadAssistancePackage.count,
        amount: reportData.roadAssistancePackage.amount,
        rowType: 'even',
      },
      {
        type: t`2. El Araç Koruma Paketi`,
        count: reportData.secondHandVehicleProtectionPackage.count,
        amount: reportData.secondHandVehicleProtectionPackage.amount,
        rowType: 'odd',
      },
      {
        type: t`e-Mağaza Harcaması`,
        count: reportData.eCommerceOrder?.count || 0,
        amount: reportData.eCommerceOrder?.amount || 0,
        rowType: 'even',
      },
      { type: t`Gider Toplam`, count: null, amount: reportData.totalExpenses, rowType: 'cardBg5' },
      { type: t`Son Bakiye`, count: null, amount: reportData.finalBalance, rowType: 'cardBg6' },
      { type: t`Kale Kart`, count: reportData.kaleCard.count, amount: reportData.kaleCard.amount, rowType: 'odd' },
      {
        type: t`Ekspertiz Tutarı`,
        count: reportData.expertiseAmount.count,
        amount: reportData.expertiseAmount.amount,
        rowType: 'even',
      },
    ];
  };

  return (
    <Container maxW="container.xl" py={5}>
      <VStack spacing={6} align="stretch">
        <Flex justify="space-between" align="center" bg={badgeBg} p={4} borderRadius="md">
          <Heading size="lg" color={headingColor}>
            <Trans>Bayi Bakiye Raporu</Trans>
          </Heading>
        </Flex>

        <Card p={4} shadow="md" borderWidth="1px" borderColor={borderColor} bg={formBg}>
          <CardBody>
            <Stack direction="column" spacing={4}>
              <HStack align="center" gap={10}>
                <StackItem>
                  <DateRangePreSelector
                    onDateRangeSelected={(startDate: Date, endDate: Date) => {
                      // Eğer tarih aralığı değiştiyse, mevcut rapor verilerini sıfırla
                      if (
                        startDate.getTime() !== selectedDates[0]?.getTime() ||
                        endDate.getTime() !== selectedDates[1]?.getTime()
                      ) {
                        setReportData(null);
                      }

                      setSelectedDates([startDate, endDate]);
                    }}
                  />
                </StackItem>
                {(authUserInfo?.isSuperAdmin || authUserInfo?.isAdmin) && (
                  <>
                    <StackItem w="200px">
                      <FormControl>
                        <FormLabel>
                          <Trans>Marka</Trans>
                        </FormLabel>
                        <Select
                          value={selectedBrand}
                          onChange={(e) => {
                            const newBrand = Number(e.target.value);
                            setSelectedBrand(newBrand);
                            setReportData(null);
                            setSelectedExpertisePoint(undefined);
                          }}
                        >
                          <option value={0}>Hepsi</option>
                          {Array.from(ExpertisePointGroupTypeLabel.entries()).map(([key, value]) => (
                            <option key={key} value={key}>
                              {value}
                            </option>
                          ))}
                        </Select>
                      </FormControl>
                    </StackItem>
                    <StackItem w="300px">
                      <ExpertisePointSelect
                        enableAll
                        groupType={selectedBrand === 0 ? undefined : selectedBrand}
                        onExpertisePointSelected={(expertisePoint) => {
                          if (
                            expertisePoint?.id !== selectedExpertisePoint?.id ||
                            (!expertisePoint && selectedExpertisePoint)
                          ) {
                            setReportData(null);
                          }
                          setSelectedExpertisePoint(expertisePoint);
                        }}
                      />
                    </StackItem>
                  </>
                )}
              </HStack>

              <Stack direction={{ base: 'column', md: 'row' }} spacing={6} align="flex-end">
                <FormControl>
                  <FormLabel fontWeight="bold" color={textColor}>
                    <Trans>Tarih Aralığı</Trans>
                  </FormLabel>
                  <LocalizedRangeDatepicker selectedDates={selectedDates} onDateChange={handleDateChange} />
                </FormControl>

                <HStack spacing={4}>
                  <Tooltip label="Raporu ara" hasArrow>
                    <Button
                      colorScheme="blue"
                      onClick={handleSearch}
                      isLoading={isLoading}
                      leftIcon={<Icon as={SearchIcon} />}
                      size="md"
                    >
                      <Trans>Ara</Trans>
                    </Button>
                  </Tooltip>
                  <Tooltip label="Excel olarak indir" hasArrow>
                    <Button
                      colorScheme="green"
                      onClick={handleExport}
                      isLoading={isExporting}
                      isDisabled={!reportData}
                      leftIcon={<Icon as={DownloadIcon} />}
                      size="md"
                    >
                      <Trans>Excel&apos;e Aktar</Trans>
                    </Button>
                  </Tooltip>
                </HStack>
              </Stack>
            </Stack>
          </CardBody>
        </Card>

        {isLoading ? (
          <LoadingIndicator />
        ) : reportData ? (
          <VStack spacing={6} align="stretch">
            <SimpleGrid columns={{ base: 1, md: 5 }} spacing={6}>
              <Card
                bg={colors.cardBg1}
                shadow="md"
                borderRadius="lg"
                overflow="hidden"
                _hover={{ transform: 'translateY(-5px)', boxShadow: 'xl' }}
                transition="all 0.3s"
              >
                <CardBody>
                  <Stat>
                    <StatLabel fontSize="md" fontWeight="semibold" color={useColorModeValue('blue.800', 'blue.100')}>
                      <Trans>Devredilen Bakiye</Trans>
                    </StatLabel>
                    <StatNumber fontSize="2xl" fontWeight="bold" color={colors.neutral}>
                      {formatCurrency(reportData.initialBalance)}
                    </StatNumber>
                    <StatHelpText color={textColor}>
                      <Trans>İlgili tarih aralığında devredilen bakiye</Trans>
                    </StatHelpText>
                  </Stat>
                </CardBody>
              </Card>
              <Card
                bg={colors.cardBg2}
                shadow="md"
                borderRadius="lg"
                overflow="hidden"
                _hover={{ transform: 'translateY(-5px)', boxShadow: 'xl' }}
                transition="all 0.3s"
              >
                <CardBody>
                  <Stat>
                    <StatLabel fontSize="md" fontWeight="semibold" color={useColorModeValue('green.800', 'green.100')}>
                      <Trans>Yüklenen Bakiye</Trans>
                    </StatLabel>
                    <StatNumber fontSize="2xl" fontWeight="bold" color={colors.positive}>
                      {formatCurrency(reportData.loadedBalance)}
                    </StatNumber>
                    <StatHelpText color={textColor}>
                      <Trans>İlgili tarih aralığında yüklenen bakiye (kredi kartı veya pos ile)</Trans>
                    </StatHelpText>
                  </Stat>
                </CardBody>
              </Card>
              <Card
                bg={colors.cardBg3}
                shadow="md"
                borderRadius="lg"
                overflow="hidden"
                _hover={{ transform: 'translateY(-5px)', boxShadow: 'xl' }}
                transition="all 0.3s"
              >
                <CardBody>
                  <Stat>
                    <StatLabel fontSize="md" fontWeight="semibold" color={useColorModeValue('blue.800', 'blue.100')}>
                      <Trans>Toplam Bakiye</Trans>
                    </StatLabel>
                    <StatNumber fontSize="2xl" fontWeight="bold" color={colors.accent}>
                      {formatCurrency(reportData.totalBalance)}
                    </StatNumber>
                    <StatHelpText color={textColor}>
                      <Trans>İlgili tarih aralığında Yüklenen Bakiye ile Devredilen Bakiyenin toplamı</Trans>
                    </StatHelpText>
                  </Stat>
                </CardBody>
              </Card>
              <Card
                bg={colors.cardBg5}
                shadow="md"
                borderRadius="lg"
                overflow="hidden"
                _hover={{ transform: 'translateY(-5px)', boxShadow: 'xl' }}
                transition="all 0.3s"
              >
                <CardBody>
                  <Stat>
                    <StatLabel fontSize="md" fontWeight="semibold" color={useColorModeValue('red.800', 'red.100')}>
                      <Trans>Gider Toplam</Trans>
                    </StatLabel>
                    <StatNumber fontSize="2xl" fontWeight="bold" color={colors.negative}>
                      {formatCurrency(reportData.totalExpenses)}
                    </StatNumber>
                    <StatHelpText color={textColor}>
                      <Trans>Dönem içindeki tüm giderler</Trans>
                    </StatHelpText>
                  </Stat>
                </CardBody>
              </Card>
              <Card
                bg={colors.cardBg6}
                shadow="md"
                borderRadius="lg"
                overflow="hidden"
                _hover={{ transform: 'translateY(-5px)', boxShadow: 'xl' }}
                transition="all 0.3s"
              >
                <CardBody>
                  <Stat>
                    <StatLabel fontSize="md" fontWeight="semibold" color={useColorModeValue('teal.800', 'teal.100')}>
                      <Trans>Son Bakiye</Trans>
                    </StatLabel>
                    <StatNumber fontSize="2xl" fontWeight="bold" color={colors.neutral}>
                      {formatCurrency(reportData.finalBalance)}
                    </StatNumber>
                    <StatHelpText color={textColor}>
                      <Trans>Dönem sonu kalan bakiye</Trans>
                    </StatHelpText>
                  </Stat>
                </CardBody>
              </Card>
            </SimpleGrid>

            <Card bg={cardBg} shadow="md" borderRadius="lg" overflow="hidden">
              <CardBody>
                <Flex justify="space-between" align="center" mb={4}>
                  <Heading size="md" color={headingColor}>
                    <Trans>Finansal Özet</Trans>
                  </Heading>
                  <HStack spacing={4}>
                    {selectedExpertisePoint && (
                      <Badge colorScheme="blue" p={2} borderRadius="md" fontSize="md">
                        <Text fontWeight="bold">
                          <Trans>Bayi</Trans>: : {selectedExpertisePoint.name || 'Seçili Bayi'}
                        </Text>
                      </Badge>
                    )}
                    {selectedDates.length === 2 && (
                      <Badge colorScheme="blue" p={2} borderRadius="md" fontSize="md">
                        <Text fontWeight="bold">
                          <Trans>Tarih</Trans>: {selectedDates[0].toLocaleDateString('tr-TR')} -{' '}
                          {selectedDates[1].toLocaleDateString('tr-TR')}
                        </Text>
                      </Badge>
                    )}
                  </HStack>
                </Flex>
                <Divider mb={4} borderColor={borderColor} />

                <DataTable
                  data={prepareTableData()}
                  columns={columns}
                  tableProps={{
                    variant: 'simple',
                    colorScheme: 'blue',
                    size: 'md',
                    sx: {
                      'thead tr': {
                        backgroundColor: colors.tableHeaderBg,
                      },
                      'thead th': {
                        color: colors.tableHeaderText,
                      },
                    },
                  }}
                />
              </CardBody>
            </Card>

            {/* e-Mağaza Sipariş Detayları */}
            {reportData.eCommerceOrderList && reportData.eCommerceOrderList.length > 0 && (
              <Card bg={cardBg} shadow="md" borderRadius="lg" overflow="hidden">
                <CardBody>
                  <Flex justify="space-between" align="center" mb={4}>
                    <Heading size="md" color={headingColor}>
                      <Trans>e-Mağaza Sipariş Detayları</Trans>
                    </Heading>
                    <Badge colorScheme="blue" p={2} borderRadius="md" fontSize="md">
                      <Text fontWeight="bold">
                        <Trans>Toplam</Trans>: {reportData.eCommerceOrderList.length} <Trans>Sipariş</Trans>
                      </Text>
                    </Badge>
                  </Flex>
                  <Divider mb={4} borderColor={borderColor} />

                  <SimpleGrid columns={1} spacing={4}>
                    {reportData.eCommerceOrderList.map((order: FinancialSummaryOrderDetailDto) => (
                      <Card key={order.orderId} bg={colors.tableBgEven} p={4} borderRadius="md" shadow="sm">
                        <HStack justify="space-between" align="center">
                          <VStack align="start" spacing={1}>
                            <Text fontWeight="bold" color={colors.neutral}>
                              {order.orderIdText}
                            </Text>
                            <Text fontSize="sm" color={textColor}>
                              <Trans>Tarih</Trans>: {new Date(order.orderDate).toLocaleDateString('tr-TR')}
                            </Text>
                          </VStack>
                          <VStack align="end" spacing={1}>
                            <Text fontWeight="bold" color={colors.negative} fontSize="lg">
                              {formatCurrency(order.totalPrice)}
                            </Text>
                            <Badge colorScheme="blue" size="sm">
                              <Trans>Sipariş</Trans> #{order.orderId}
                            </Badge>
                          </VStack>
                        </HStack>
                      </Card>
                    ))}
                  </SimpleGrid>
                </CardBody>
              </Card>
            )}
          </VStack>
        ) : (
          <Card p={6} shadow="md" borderWidth="1px" borderColor={borderColor} bg={cardBg} textAlign="center">
            <Icon as={InfoIcon} boxSize={10} color={colors.neutral} mb={4} />
            <Text fontSize="lg" color={textColor}>
              <Trans>Lütfen bayi seçiniz ve tarih aralığını belirleyiniz. Sonra arama yapınız.</Trans>
            </Text>
          </Card>
        )}
      </VStack>
    </Container>
  );
};
